import { UpgradeModel } from "types";

import astralSightImage from "assets/upgrade-astral-sight.png";
import atomicDriveImage from "assets/upgrade-atomic-drive.png";
import bastionEnergaImage from "assets/upgrade-bastion-energa.png";
import cosmicSightImage from "assets/upgrade-cosmic-sight.png";
import dashEnergaImage from "assets/upgrade-dash-energa.png";
import flashEnergaImage from "assets/upgrade-flash-energa.png";
import infernoEnergaImage from "assets/upgrade-inferno-energa.png";
import lockEnergaImage from "assets/upgrade-lock-energa.png";
import matrixInfiniteImage from "assets/upgrade-matrix-infinite.png";
import matrixUltimaImage from "assets/upgrade-matrix-ultima.png";
import megaCoreImage from "assets/upgrade-mega-core.png";
import novaFortressImage from "assets/upgrade-nova-fortress.png";
import novaWardImage from "assets/upgrade-nova-ward.png";
import quantumDriveImage from "assets/upgrade-quantum-drive.png";
import razorEnergaImage from "assets/upgrade-razor-energa.png";
import surgeCoreImage from "assets/upgrade-surge-core.png";
import tempestEnergaImage from "assets/upgrade-tempest-energa.png";
import voidBeamImage from "assets/upgrade-void-beam.png";
import voidLancerImage from "assets/upgrade-void-lancer.png";
import wardEnergaImage from "assets/upgrade-ward-energa.png";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const SENECA_MERIDIAN_UPGRADES: Record<string, UpgradeModel> = {
  // WEAPONS
  void_beam: {
    slug: "void_beam",
    name: "Void Beam",
    description:
      "A powerful antimatter laser that increases your max damage and also your pilot skill power/speed.",
    image: voidBeamImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 24,
      energy: 12,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
      energyMultiplier: 1,
    },
  },
  void_lancer: {
    slug: "void_lancer",
    name: "Void Lancer",
    description:
      "A powerful antimatter laser that increases your max damage and also your pilot skill power/speed.",
    image: voidLancerImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 27,
      energy: 14,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxWeakenParts: 1,
      energyMultiplier: 1,
    },
  },
  // SHIELDS
  nova_fortress: {
    slug: "nova_fortress",
    name: "Nova Fortress",
    description:
      "A powerful antimatter force field increases your ship's health and shields as well as your pilot skill power.",
    image: novaFortressImage,
    animation: shieldsAnimation,
    animationSpeed: 3600,
    part: "shields",
    requirements: {
      resilience: 18,
      energy: 9,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 1,
      energyMultiplier: 1,
    },
  },
  nova_ward: {
    slug: "nova_ward",
    name: "Nova Ward",
    description:
      "A powerful antimatter force field increases your ship's health and shields as well as your pilot skill power.",
    image: novaWardImage,
    animation: shieldsAnimation,
    animationSpeed: 3600,
    part: "shields",
    requirements: {
      resilience: 22,
      energy: 11,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
      energyMultiplier: 1,
    },
  },
  // THRUSTERS
  atomic_drive: {
    slug: "atomic_drive",
    name: "Atomic Drive",
    description:
      "Powerful energy thrusters that elevate the frequency of your attacks as well as recharges your pilot skills faster. Speed on speed.",
    image: atomicDriveImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 20,
      energy: 10,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 1,
      energyMultiplier: 1,
    },
  },
  quantum_drive: {
    slug: "quantum_drive",
    name: "Quantum Drive",
    description:
      "Powerful energy thrusters that elevate the frequency of your attacks as well as recharges your pilot skills faster. Speed on speed.",
    image: quantumDriveImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 24,
      energy: 12,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 1,
      fasterRecharge: 1,
    },
  },
  // TARGETING SYSTEM
  cosmic_sight: {
    slug: "cosmic_sight",
    name: "Cosmic Sight",
    description:
      "An antimatter-based targeting system upgrade that helps you land attacks more consistently and increases your pilot skill power.",
    image: cosmicSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 16,
      energy: 8,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 1,
      energyMultiplier: 1,
    },
  },
  astral_sight: {
    slug: "astral_sight",
    name: "Astral Sight",
    description:
      "An antimatter-based targeting system upgrade that helps you land attacks more consistently and increases your pilot skill power.",
    image: astralSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 32,
      energy: 16,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 1,
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  // REACTOR CORE
  ward_energa: {
    slug: "ward_energa",
    name: "Ward Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your pilot skill recharge speed and ship protection.",
    image: wardEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 37,
      resilience: 19,
    },
    grade: "elite",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      damageReduction: 1,
    },
  },
  bastion_energa: {
    slug: "bastion_energa",
    name: "Bastion Energa",
    description:
      "An extremely powerful upgrade to your reactor core that boosts your skill power and also deflects damage.",
    image: bastionEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 41,
      resilience: 21,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      maxHealth: 1,
    },
  },
  dash_energa: {
    slug: "dash_energa",
    name: "Dash Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and attack/evasion speed.",
    image: dashEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 42,
      speed: 21,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
  },
  flash_energa: {
    slug: "flash_energa",
    name: "Flash Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your skill  power and attack evasion.",
    image: flashEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 46,
      speed: 23,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackEvasion: 1,
    },
  },
  tempest_energa: {
    slug: "tempest_energa",
    name: "Tempest Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power/speed and ability to weaken opponent parts.",
    image: tempestEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 47,
      firepower: 24,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      maxWeakenParts: 1,
    },
  },
  inferno_energa: {
    slug: "inferno_energa",
    name: "Inferno Energa",
    description:
      "A very powerful upgrade to your reactor core that boosts your skill power and max attack damage.",
    image: infernoEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 52,
      firepower: 26,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
  },
  razor_energa: {
    slug: "razor_energa",
    name: "Razor Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed/power and minimum damage.",
    image: razorEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 55,
      precision: 28,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
      attackAccuracy: 1,
    },
  },
  lock_energa: {
    slug: "lock_energa",
    name: "Lock Energa",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and attack accuracy and minimum damage.",
    image: lockEnergaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 61,
      precision: 32,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
  },
  surge_core: {
    slug: "surge_core",
    name: "Surge Core",
    description:
      "An upgrade to your reactor core that boosts your skill recharging speed and power.",
    image: surgeCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 64,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  mega_core: {
    slug: "mega_core",
    name: "Mega Core",
    description:
      "An solid upgrade to your reactor core that boosts your skill recharging power and speed.",
    image: megaCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 71,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
  matrix_infinite: {
    slug: "matrix_infinite",
    name: "MATRIX/INFINITE",
    description: "Uh, this thing is kind of intense.",
    image: matrixInfiniteImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 78,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  matrix_ultima: {
    slug: "matrix_ultima",
    name: "MATRIX//ULTIMA",
    description: "THIS CORE IS REAL POWERFUL. THIS CORE IS STRESSING ME OUT.",
    image: matrixUltimaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 86,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
};

export default SENECA_MERIDIAN_UPGRADES;
